import type { ToplistingPromotionCardConfig } from '@/components/ToplistingPromotionCards/types';
import {
  TOPLISTING_PROMOTION_CARD_ACCENT_LIFETIME,
  TOPLISTING_PROMOTION_CARD_ACCENT_PREMIUM,
  TOPLISTING_PROMOTION_CARD_ACCENT_TOP,
  TOPLISTING_PROMOTION_CARD_ACCENT_TOP_ABO,
} from '@/constants/toplistingPromotion';
import { ToplistingVariant } from '@/types';

export interface BuildToplistingPromotionStepCardsParams {
  isTLAboContractActive: boolean;
  isTopAboRadioButtonEnabled: boolean;
  shouldDisplayPricingSlot: boolean;
  shouldDisplayLifetimePricingSlot: boolean;
}

/**
 * Always render three cards —
 * Premium, **either** Top Listing (Abo) **or** daily Top Listing (never both), Lifetime — using
 * {@link BuildToplistingPromotionStepCardsParams.isTopAboRadioButtonEnabled} (free slot or edit
 * on existing ABO) to choose the middle column. Slots copy on the Abo card stays in the booking
 * form `#pricing-TOP_ABO` slot.
 */
export function buildToplistingPromotionStepCards(
  params: BuildToplistingPromotionStepCardsParams,
): ToplistingPromotionCardConfig[] {
  const list: ToplistingPromotionCardConfig[] = [];
  list.push({
    i18nKey: 'PREMIUM',
    value: ToplistingVariant.Premium,
    showPricingSlot: params.shouldDisplayPricingSlot,
    displayFullDescription: false,
    accentColor: TOPLISTING_PROMOTION_CARD_ACCENT_PREMIUM,
  });

  if (params.isTopAboRadioButtonEnabled) {
    list.push({
      i18nKey: 'TOP_ABO',
      value: ToplistingVariant.TopAbo,
      disabled: false,
      showPricingSlot: true,
      displayFullDescription: false,
      accentColor: TOPLISTING_PROMOTION_CARD_ACCENT_TOP_ABO,
    });
  } else {
    list.push({
      i18nKey: 'TOP',
      value: ToplistingVariant.Top,
      showPricingSlot: params.shouldDisplayPricingSlot,
      displayFullDescription: false,
      accentColor: TOPLISTING_PROMOTION_CARD_ACCENT_TOP,
      recommended: true,
    });
  }

  list.push({
    i18nKey: 'LIFETIME',
    value: ToplistingVariant.Lifetime,
    showPricingSlot: params.shouldDisplayLifetimePricingSlot,
    displayFullDescription: true,
    accentColor: TOPLISTING_PROMOTION_CARD_ACCENT_LIFETIME,
  });

  return list;
}

export interface BuildToplistingLifetimeReadonlyStepCardsParams {
  shouldDisplayLifetimePricingSlot: boolean;
}

/** Lifetime-only row (modal read-only lifetime booking). */
export function buildToplistingLifetimeReadonlyStepCards(
  params: BuildToplistingLifetimeReadonlyStepCardsParams,
): ToplistingPromotionCardConfig[] {
  return [
    {
      i18nKey: 'LIFETIME',
      value: ToplistingVariant.Lifetime,
      showPricingSlot: params.shouldDisplayLifetimePricingSlot,
      displayFullDescription: true,
      accentColor: TOPLISTING_PROMOTION_CARD_ACCENT_LIFETIME,
    },
  ];
}
